import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { InitialService } from '../../../services/initial.service';
import {
  Cluster,
  ClusterStats,
  GoogleMap,
  GoogleMapsModule,
  MapHeatmapLayer,
  MapInfoWindow,
  MapMarker,
  MapMarkerClusterer,
  Renderer,
} from '@angular/google-maps';
import { GeoLocationService } from '../../../services/geo-location-service.service';
import { assets } from '../../../core/constants/assets.const';
import { driverService } from '../../../services/driver.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { heatMap } from '../../../models/driver.model';
import { NavigationEnd, Router } from '@angular/router';
import { driverMapService } from '../../../services/driverMap.service';
import { filter } from 'rxjs';
import { EncryptionService } from '../../../services/encryption.service';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';

interface CustomMarker {
  position: google.maps.LatLngLiteral;
  icon: google.maps.Icon;
  title?: string;
}

@Component({
  selector: 'logibids-google-map',
  imports: [
    GoogleMap,
    GoogleMapsModule,
    MapMarker,
    CommonModule,
    MapHeatmapLayer,
    MapMarkerClusterer,
    MapInfoWindow,
  ],
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
})
export class GoogleMapComponent implements AfterViewInit, OnInit {
  lat = signal<number>(0);
  lng = signal<number>(0);
  zoom = signal<number>(16);
  @ViewChild(GoogleMap, { static: false }) googleMap!: GoogleMap;

  center = signal<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });
  markerPosition = signal<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });
  options = signal<google.maps.MapOptions>({
    center: this.center(),
    zoom: this.zoom(),
    maxZoom: 18,
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
  });
  private initialService = inject(InitialService);
  googleMapsReady = this.initialService.googleMapsReady;

  markerIcon = computed(() => {
    if (!this.googleMapsReady() || !window.google?.maps?.Size) return null;
    return {
      url: assets.MapMarker,
      scaledSize: new google.maps.Size(40, 40), // Adjust size as needed
    };
  });
  private locationService = inject(GeoLocationService);
  private _driverService = inject(driverService);
  coords = this.locationService.currentCoords;
  private router = inject(Router);
  isDriverHomeRoute = signal(false);
  isHeatMap = signal(false);
  isFlagMap = signal(false); // true when flag map (markers) should be shown
  markers = signal<CustomMarker[]>([]);
  heatmapOptions = signal<google.maps.visualization.HeatmapLayerOptions>({
    radius: 50,
    opacity: 0.6,
    gradient: [
      'rgba(0,255,0,0)', // transparent green
      'rgba(0,255,0,1)', // green (Normal)
      'rgba(255,165,0,1)', // orange (Busy)
      'rgba(255,0,0,1)', // red (Very Busy)
    ],
  });

  clickedMarker = signal<CustomMarker | null>(null);
  infoWindows: google.maps.InfoWindow[] = [];
  userHeatCoordinates = signal<google.maps.visualization.WeightedLocation[]>(
    []
  );
  heatMapMarkers = signal<
    { position: google.maps.LatLngLiteral; amount: number }[]
  >([]);
  private _authService = inject(AuthService);
  user = computed(() => this._authService.profileDetails());
  @ViewChildren(MapMarker) markerRefs!: QueryList<MapMarker>;
  @ViewChildren(MapInfoWindow) infoWindowS!: QueryList<MapInfoWindow>;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow!: MapInfoWindow;
  customRenderer = computed<Renderer | undefined>(() => {
    if (
      !this.googleMapsReady() ||
      !window.google?.maps?.Marker ||
      !window.google?.maps?.Size
    )
      return undefined;
    return {
      render: (cluster: Cluster, stats: ClusterStats, map: google.maps.Map) => {
        const count = cluster.markers?.length || 0;

        // Use your custom image instead of default blue marker
        const marker = new google.maps.Marker({
          position: cluster.position,
          icon: {
            url: assets.flag, // Your custom cluster image
            scaledSize: new google.maps.Size(40, 40), // Adjust size as needed
          },
          map: map,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });

        return marker;
      },
    };
  });

  transparentMarkerIcon = computed<google.maps.Icon | undefined>(() => {
    if (
      !this.googleMapsReady() ||
      !window.google?.maps?.Size ||
      !window.google?.maps?.Point
    )
      return undefined;
    return {
      url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=', // 1x1 transparent PNG
      scaledSize: new google.maps.Size(1, 1),
      anchor: new google.maps.Point(0, 0),
    };
  });

  constructor(
    private destroy: DestroyRef,
    private _driverMapService: driverMapService,
    private cd: ChangeDetectorRef,
    private _encryption: EncryptionService
  ) {
    // Initialize based on current URL to handle direct landings or early initializations
    const initialUrl = this.router.url;
    this.isDriverHomeRoute.set(
      initialUrl === '/driver-home' || initialUrl.startsWith('/driver-home')
    );

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe((event: NavigationEnd) => {
        const isDriverHome =
          event.urlAfterRedirects === '/driver-home' ||
          event.urlAfterRedirects.startsWith('/driver-home');
        this.isDriverHomeRoute.set(isDriverHome);
      });

    // Clear markers and service state when leaving driver-home route to ensure "fresh" state on return
    effect(() => {
      if (!this.isDriverHomeRoute()) {
        this.removeMarkers();
        this.clickedMarker.set(null);
        this.userHeatCoordinates.set([]);
        this.heatMapMarkers.set([]);
        this.isHeatMap.set(false);
        this.isFlagMap.set(false);

        // Reset service states to null to avoid stale data when returning
        this._driverMapService.setFlagMap(null as any);
        this._driverMapService.setHeatMap(null as any);
        this._driverMapService.setDriverHomeCoord(null as any);
      }
    });
    effect(() => {
      const current = this.coords();
      if (current) {
        this.lat.set(current.latitude);
        this.lng.set(current.longitude);
        if (this.lat() > 0 && this.lng() > 0) {
          this.center.set({ lat: this.lat(), lng: this.lng() });
          this.markerPosition.set({ lat: this.lat(), lng: this.lng() });
          // Ensure mapId is properly used
          this.options.set({
            center: this.center(),
            zoom: this.zoom(),
            disableDefaultUI: true,
            zoomControl: false,
            mapTypeControl: false,
            streetViewControl: false,
          });
        }
      }
    });

    effect(() => {
      const flagMap = this._driverMapService.getFlagMap();
      const heatMap = this._driverMapService.getHeatMap();
      if (flagMap() !== null) {
        if (flagMap() === true) {
          // show markers (flags)
          this.isFlagMap.set(true);
          this.isHeatMap.set(false); // mutually exclusive
          this.getMarkers(); // fetch markers
        } else {
          // hide markers if flagMap turned off
          this.clickedMarker.set(null);
          this.isFlagMap.set(false);
          this.removeMarkers();
        }
      }

      if (heatMap() !== null) {
        if (heatMap() === true) {
          this.isHeatMap.set(true);
          this.isFlagMap.set(false);
          this.removeMarkers();
          this.getUserLocations();
        } else {
          this.clickedMarker.set(null);
          this.isHeatMap.set(false);
          // when heatmap disabled, do nothing to markers here; flags control them
        }
      }
    });
  }

  ngOnInit() {
    const checkZoom = (url: string) => {
      const zoom6Routes = [
        '/auth',
        '/get-started',
        '/sp-profile',
        '/my-profile',
        '/sp',
        '/payment',
        '/wallet',
        '/account-control',
        '/public',
        '/sp-bids',
      ];

      const isWideView = zoom6Routes.some(route => url.startsWith(route));
      this.zoom.set(isWideView ? 6 : 16);
    };

    // Initialize zoom based on current URL
    checkZoom(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((event: any) => {
        checkZoom(event.url);
      });
  }

  ngAfterViewInit(): void {
    // Subscribe to QueryList changes
    this.markerRefs.changes.subscribe(() => this.tryOpenAllInfoWindowsSafely());

    // Initial run after first render
    queueMicrotask(() => this.tryOpenAllInfoWindowsSafely());
  }

  closeAllInfoWindows() {
    if (this.infoWindow) {
      this.infoWindow.close();
    }
    if (this.infoWindowS && this.infoWindowS.length > 0) {
      this.infoWindowS.forEach(infoWindow => {
        infoWindow.close();
      });
    }
  }

  private tryOpenAllInfoWindowsSafely(retries = 6): void {
    // Ensure template bindings are applied
    this.cd.detectChanges();

    const markers = this.markerRefs ? this.markerRefs.toArray() : [];
    const infoWindows = this.infoWindowS ? this.infoWindowS.toArray() : [];

    // If we don't have enough info windows yet, wait shortly and retry (defensive)
    if (infoWindows.length < markers.length) {
      if (retries > 0) {
        // small delay to allow DOM / component children to render
        setTimeout(() => this.tryOpenAllInfoWindowsSafely(retries - 1), 120);
      }
      return;
    }

    // Open each info window for its marker (only if both exist)
    markers.forEach((marker, idx) => {
      const infowin = infoWindows[idx];
      if (marker && infowin) {
        try {
          // open accepts a MapMarker (marker) to anchor the window
          infowin.open(marker);
        } catch (err) {
          // swallow errors silently — better than breaking rendering
          console.warn('Failed to open info window', err);
        }
      }
    });
  }

  getUserLocations(): void {
    this._driverService
      .getUserLocations()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(
        (res: heatMap[]) => {
          if (!res?.length) {
            this.userHeatCoordinates.set([]);
            this.heatMapMarkers.set([]);
            return;
          }

          const weights = res.map(r => Number(r.weight ?? 1));
          const maxWeight = Math.max(...weights, 1);

          const heatmapProcessed: google.maps.visualization.WeightedLocation[] =
            res
              .map(item => {
                const lat = Number(item.lat);
                const lng = Number(item.long);
                const amount = Number(item.amount);
                const weight = Number(item.weight ?? 1);

                if (
                  !Number.isFinite(lat) ||
                  !Number.isFinite(lng) ||
                  amount < 0
                ) {
                  return null;
                }

                return {
                  location: window.google?.maps?.LatLng
                    ? new google.maps.LatLng(lat, lng)
                    : ({ lat, lng } as any),
                  weight: weight / maxWeight,
                };
              })
              .filter(
                (v): v is google.maps.visualization.WeightedLocation =>
                  v !== null
              );
          this.userHeatCoordinates.set(heatmapProcessed);

          const markerProcessed = res
            .map(item => {
              const lat = +item.lat;
              const lng = +item.long;
              const amount = item.amount;
              if (!isFinite(lat) || !isFinite(lng) || amount <= 0) return null;

              return {
                position: { lat, lng },
                amount: Number(item.amount),
              };
            })
            .filter(Boolean);

          this.heatMapMarkers.set(markerProcessed);
          queueMicrotask(() => this.tryOpenAllInfoWindowsSafely());
        },
        err => {
          console.error('Failed to load heatmap points', err);
          this.userHeatCoordinates.set([]);
          this.heatMapMarkers.set([]);
        }
      );
  }

  getMarkers(): void {
    this._driverService
      .getUserLocationsFlag()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(
        (res: heatMap[]) => {
          // Map each result to a CustomMarker object
          const mapped: CustomMarker[] = res.map(item => ({
            position: { lat: +item.lat, lng: +item.long },
            icon: {
              // Pick icon conditionally (example logic — update as needed)
              url:
                item.schedule_type === 'SCHEDULE'
                  ? assets.scheduleFlag
                  : assets.flag,
              scaledSize: window.google?.maps?.Size
                ? new google.maps.Size(40, 40)
                : (undefined as any),
              anchor: window.google?.maps?.Point
                ? new google.maps.Point(20, 40)
                : (undefined as any), // center bottom anchor
            },
          }));

          // Remove duplicates by lat/lng pair
          const unique = mapped.reduce<CustomMarker[]>((acc, cur) => {
            const key = `${cur.position.lat},${cur.position.lng}`;
            if (!acc.some(a => `${a.position.lat},${a.position.lng}` === key)) {
              acc.push(cur);
            }
            return acc;
          }, []);

          this.markers.set(unique);
        },
        err => {
          console.error('Failed to load markers', err);
          this.markers.set([]);
        }
      );
  }

  removeMarkers(): void {
    this.markers.set([]);
  }

  onMarkerClick(marker: CustomMarker): void {
    if (marker.position.lat && marker.position.lng) {
      const destLat = marker.position.lat;
      const destLng = marker.position.lng;

      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=Current+Location&destination=${destLat},${destLng}&travelmode=driving`;

      // Open in a new browser tab
      window.open(googleMapsUrl, '_blank');
    } else {
      console.warn('Current location not available.');
    }
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (!this.isDriverHomeRoute?.()) {
      return;
    }

    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (
      typeof lat !== 'number' ||
      typeof lng !== 'number' ||
      !isFinite(lat) ||
      !isFinite(lng)
    ) {
      console.warn('Map click had no lat/lng:', event);
      return;
    }

    // show a visible marker for the clicked point
    this.clickedMarker.set({
      position: { lat, lng },
      icon: {
        url: assets.dragMarker, // or a different icon for clicked point
        scaledSize: window.google?.maps?.Size
          ? new google.maps.Size(40, 40)
          : (undefined as any),
        anchor: window.google?.maps?.Point
          ? new google.maps.Point(20, 40)
          : (undefined as any),
      },
    });

    this._driverMapService.setDriverHomeCoord({ lat, lng });
  }

  // --- Hide markers and disable Flag Map mode ---
  hideFlagMarkers(): void {
    this.isFlagMap.set(false);
    this.removeMarkers();
  }
}
